import * as React from 'react';
import { twMerge } from 'tailwind-merge';

import { FolderIconVariant } from '../../assets/icons/folder-icon';
import { FolderIcon } from '../../assets/icons/index';
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '../popover/index';
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './breadcrumb';
import {
  BreadcrumbSegmentKind,
  BreadcrumbTrailItem,
  collapseBreadcrumbItems,
  fittingMaxVisible,
} from './breadcrumb-trail.util';

// A label this short stays whole; only longer ones give way, so a crumb like "Home" never reads "H…".
const MIN_CUT_LABEL_LENGTH = 12;

export type BreadcrumbTrailProps = {
  className?: string;
  ellipsisLabel: string;
  items: BreadcrumbTrailItem[];
  label: string;
  maxVisible?: number;
  onSelect: (id: string) => void;
};

export const BreadcrumbTrail = ({
  className,
  ellipsisLabel,
  items,
  label,
  maxVisible,
  onSelect,
}: BreadcrumbTrailProps) => {
  const navRef = React.useRef<HTMLElement>(null);
  const measureRef = React.useRef<HTMLDivElement>(null);
  // Everything, until the row has been measured: a first paint that folds the trail away and
  // then opens it again reads worse than one frame of a trail that is too wide.
  const [fittedMaxVisible, setFittedMaxVisible] = React.useState(items.length);
  // The labels, not the array: the parent builds a fresh one every render, and resubscribing the
  // observer each time would cost more than the measurement it is there to trigger.
  const labelsKey = items.map(({ label: crumb }) => crumb).join('\u0000');

  /**
   * What the row can hold at the width it has now, remeasured whenever that width changes.
   *
   * The crumbs are measured in a copy of the row laid out beside it and never painted, because
   * the visible row only ever holds the segments it was last told to, and asking it how wide the
   * hidden ones would be is a question it cannot answer.
   *
   * A caller that names `maxVisible` has decided the question itself — the mobile trail does —
   * and nothing is measured for it.
   */
  React.useLayoutEffect(() => {
    const nav = navRef.current;
    const measure = measureRef.current;

    if (maxVisible !== undefined || !nav || !measure) return;

    const fit = () =>
      setFittedMaxVisible(
        fittingMaxVisible({
          available: nav.clientWidth,
          ellipsisWidth: measure.querySelector('[data-measure="ellipsis"]')?.clientWidth ?? 0,
          itemWidths: [...measure.querySelectorAll('[data-measure="crumb"]')].map(
            (crumb) => crumb.getBoundingClientRect().width,
          ),
          separatorWidth: measure.querySelector('[data-measure="separator"]')?.clientWidth ?? 0,
        }),
      );

    fit();

    const observer = new ResizeObserver(fit);

    observer.observe(nav);

    return () => observer.disconnect();
  }, [labelsKey, maxVisible]);

  const segments = collapseBreadcrumbItems(items, maxVisible ?? fittedMaxVisible);
  const lastItemId = items[items.length - 1]?.id;

  return (
    <Breadcrumb aria-label={label} className={twMerge('relative min-w-0', className)} ref={navRef}>
      {/* The same crumbs at their natural width, out of the flow and out of the tree a reader
          walks, so the row above can be measured before it is cut down. */}
      <div
        aria-hidden
        className="pointer-events-none invisible absolute flex gap-2 text-xs font-[450] leading-4"
        ref={measureRef}
      >
        {items.map((item) => (
          <span className="whitespace-nowrap" data-measure="crumb" key={item.id}>
            {item.label}
          </span>
        ))}
        <span data-measure="separator">/</span>
        <span className="flex size-4" data-measure="ellipsis" />
      </div>
      <BreadcrumbList className="flex-nowrap overflow-hidden">
        {segments.map((segment, index) => {
          const separator =
            index > 0 ? (
              <BreadcrumbSeparator className="shrink-0" key={`separator-${index}`} />
            ) : null;

          if (segment.kind === BreadcrumbSegmentKind.ELLIPSIS) {
            return (
              <React.Fragment key="ellipsis">
                {separator}
                <BreadcrumbItem className="shrink-0">
                  <Popover>
                    <PopoverTrigger
                      aria-label={ellipsisLabel}
                      className="flex size-4 items-center justify-center rounded-[2.667px] outline-none data-[state=open]:bg-white data-[state=open]:shadow-[0px_1.6px_1.6px_rgba(16,24,40,0.06),0px_3.2px_3.2px_rgba(16,24,40,0.1)]"
                    >
                      <BreadcrumbEllipsis />
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-fit flex-col items-stretch gap-0.5 rounded border-0 p-2 shadow-[0px_-4px_16px_0px_rgba(16,24,40,0.04),0px_24px_48px_-12px_rgba(16,24,40,0.18)]"
                      sideOffset={4}
                    >
                      {segment.items.map((item) => (
                        <PopoverClose
                          className="flex h-9 items-center gap-2 rounded p-1.5 text-left text-sm font-normal leading-5 text-gray-700 hover:bg-brand-25"
                          key={item.id}
                          onClick={() => onSelect(item.id)}
                        >
                          <FolderIcon
                            className="size-4"
                            height={16}
                            variant={FolderIconVariant.OUTLINE}
                            width={16}
                          />
                          <span className="whitespace-nowrap">{item.label}</span>
                        </PopoverClose>
                      ))}
                    </PopoverContent>
                  </Popover>
                </BreadcrumbItem>
              </React.Fragment>
            );
          }

          const isCurrent = segment.item.id === lastItemId;

          return (
            <React.Fragment key={segment.item.id}>
              {separator}
              {/* What gives way first when the row is still too narrow after folding. A short
                  label is never cut; of the rest, the crumbs behind give up their width almost
                  entirely before the one the reader is standing in gives up any, so the current
                  folder stays readable and nothing is pushed off the end. */}
              <BreadcrumbItem
                className={
                  segment.item.label.length < MIN_CUT_LABEL_LENGTH
                    ? 'shrink-0'
                    : isCurrent
                      ? 'min-w-0 shrink'
                      : 'min-w-0 shrink-[999]'
                }
              >
                {isCurrent ? (
                  <BreadcrumbPage className="truncate" title={segment.item.label}>
                    {segment.item.label}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild className="truncate">
                    <button
                      onClick={() => onSelect(segment.item.id)}
                      title={segment.item.label}
                      type="button"
                    >
                      {segment.item.label}
                    </button>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
